/* ============================================================
   REGEN Clinic — Skincare Consultation Gate
   ------------------------------------------
   Site-wide gate that intercepts Add-to-Cart and Checkout for
   ZO Skin Health and Obagi products until the customer has
   completed the skincare consultation.

   Required globals (set by Liquid snippets):
     window.regenConsultStatus         (always — from theme.liquid <head>)
     window.regenProductRequiresConsult (only on gated product pages)
     window.regenProductHandle          (only on gated product pages)

   Behaviour:
     - On product pages: if product is gated and no valid consultation,
       intercept the Add-to-Cart button and route to consultation page.
     - On cart pages: if any cart item is gated and no valid consultation,
       intercept Checkout button and route to consultation page.
     - Banner appears on gated product/cart pages when gate is active.

   Performance: cart-item tag check uses a small in-memory cache.
   ============================================================ */
(function() {
  'use strict';

  const STORAGE_KEY = 'regen_skincare_consultation_v1';
  const CACHE = {}; // product handle -> { gated: bool, fetchedAt: ms }
  const CACHE_TTL = 5 * 60 * 1000; // 5 mins

  const status = window.regenConsultStatus || {};
  const consultationUrl = status.consultationPageUrl || '/pages/skincare-consultation';
  const validityMonths = status.validityMonths || 12;

  /* ========== Determine consultation validity ========== */
  function hasValidConsultation() {
    // Layer 1 — bypass tag (existing in-clinic clients)
    if (status.isLoggedIn && status.customerTags && hasBypassTag(status.customerTags)) {
      return true;
    }

    // Layer 2 — submitted online consultation, within validity window
    let completion = null;
    if (status.isLoggedIn && status.completion) {
      completion = status.completion;
    } else {
      try {
        const ls = localStorage.getItem(STORAGE_KEY);
        if (ls) completion = JSON.parse(ls);
      } catch (e) {}
    }

    if (!completion || !completion.completed_at) return false;

    const completedAt = new Date(completion.completed_at);
    if (isNaN(completedAt.getTime())) return false;

    const expires = new Date(completedAt);
    expires.setMonth(expires.getMonth() + validityMonths);
    return expires > new Date();
  }

  function hasBypassTag(tags) {
    // Shopify exposes tags as a comma-separated string (Liquid { customer.tags | json } gives an array)
    // SPLIT 2026-08-15: the gate (which applies only to `requires-consultation`
    // clinical items — retinols, tretinoin, Nu-Derm) now unlocks on the
    // `pom-cleared` customer tag, written only after clearance by Dr Chris.
    // `consultation-on-file` remains a known-client record marker (it is
    // bulk-applied by Fresha imports) but no longer clears prescription-strength
    // purchase. The online consultation completion path below is unchanged.
    const arr = Array.isArray(tags) ? tags : String(tags).split(',').map(t => t.trim());
    return arr.indexOf('pom-cleared') !== -1;
  }

  function consultationFlaggedForReview() {
    let completion = null;
    if (status.isLoggedIn && status.completion) {
      completion = status.completion;
    } else {
      try {
        const ls = localStorage.getItem(STORAGE_KEY);
        if (ls) completion = JSON.parse(ls);
      } catch (e) {}
    }
    return completion && completion.flag_required_review === true;
  }

  /* ========== Cart attribute sync ========== */
  // When the on-file consultation is flagged, attach a clinical_review_required
  // attribute to the cart so it carries through to the order. Shopify Flow then
  // tags the order awaiting-clinical-review.
  async function syncCartAttribute() {
    if (!hasValidConsultation()) return;
    const needsReview = consultationFlaggedForReview();
    try {
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          attributes: {
            clinical_review_required: needsReview ? 'true' : 'false'
          }
        })
      });
    } catch (e) {
      // Non-fatal — checkout will still succeed; the order just won't be tagged.
    }
  }

  /* ========== Build redirect URL ========== */
  function gateRedirectUrl(returnTo, variantId) {
    const u = new URL(consultationUrl, window.location.origin);
    if (returnTo) u.searchParams.set('return_to', returnTo);
    if (variantId) u.searchParams.set('variant_id', String(variantId));
    return u.toString();
  }

  /* Pull the currently-selected variant ID from a Shopify product form. */
  function getVariantIdFromForm(form) {
    if (!form) return null;
    const input = form.querySelector('input[name="id"]');
    if (input && input.value) return input.value;
    const select = form.querySelector('select[name="id"]');
    if (select && select.value) return select.value;
    return null;
  }

  /* ========== Modal: friendly intercept rather than instant redirect ========== */
  function showGateModal({ productTitle, onProceed, onCancel }) {
    const wrap = document.createElement('div');
    wrap.className = 'regen-gate-modal';
    wrap.innerHTML = `
      <div class="regen-gate-modal__backdrop" data-cancel></div>
      <div class="regen-gate-modal__panel" role="dialog" aria-labelledby="regen-gate-title">
        <p class="regen-gate-modal__kicker">REGEN Clinic</p>
        <h2 id="regen-gate-title">A short consultation first</h2>
        <p>${productTitle ? `<strong>${productTitle}</strong> is part of our medical-grade range.` : 'The items in your basket are part of our medical-grade range.'} Before your first order, we ask you to complete a short skincare consultation so our clinical team can ensure the products are right for you.</p>
        <p class="regen-gate-modal__time">Takes 5–7 minutes · Saved to your account for 12 months.</p>
        <div class="regen-gate-modal__actions">
          <button type="button" class="regen-gate-modal__btn regen-gate-modal__btn--ghost" data-cancel>Cancel</button>
          <button type="button" class="regen-gate-modal__btn regen-gate-modal__btn--primary" data-proceed>Begin consultation</button>
        </div>
      </div>
    `;
    document.body.appendChild(wrap);
    document.body.style.overflow = 'hidden';

    const cleanup = () => {
      document.body.style.overflow = '';
      wrap.remove();
    };

    wrap.querySelectorAll('[data-cancel]').forEach(el => {
      el.addEventListener('click', () => {
        cleanup();
        if (onCancel) onCancel();
      });
    });
    wrap.querySelector('[data-proceed]').addEventListener('click', () => {
      cleanup();
      if (onProceed) onProceed();
    });
  }

  /* ========== Banner shown when gate applies ========== */
  function injectBanner({ container, productTitle, variantId }) {
    if (container.querySelector('.regen-gate-banner')) return;
    const banner = document.createElement('div');
    banner.className = 'regen-gate-banner';
    const isLoggedIn = !!status.isLoggedIn;
    const cta = isLoggedIn ? 'Begin the 5-minute consultation →' : 'Sign in to begin your 5-minute consultation →';
    banner.innerHTML = `
      <p><strong>Doctor-led skincare.</strong> ${productTitle ? `<em>${productTitle}</em> requires a short consultation` : 'Items in your basket require a short consultation'} before your first order. <a href="${gateRedirectUrl(window.location.pathname, variantId)}">${cta}</a></p>
    `;
    container.prepend(banner);
  }

  /* ========== Product page gating ========== */
  function attachProductGate() {
    if (!window.regenProductRequiresConsult) return;
    if (hasValidConsultation()) return;

    const productTitle = window.regenProductTitle || '';

    // Banner — find the product form and prepend
    const productForm = document.querySelector('form[action*="/cart/add"]');
    const variantId = getVariantIdFromForm(productForm);
    if (productForm && productForm.parentElement) {
      injectBanner({ container: productForm.parentElement, productTitle: productTitle, variantId: variantId });
    }

    // Intercept ATC submit
    if (productForm) {
      productForm.addEventListener('submit', (e) => {
        if (hasValidConsultation()) return;
        e.preventDefault();
        e.stopImmediatePropagation();
        const currentVariantId = getVariantIdFromForm(productForm);
        showGateModal({
          productTitle: productTitle,
          onProceed: () => {
            window.location.href = gateRedirectUrl(window.location.pathname, currentVariantId);
          }
        });
      }, true);
    }

    // Also intercept clicks on common ATC button selectors (some themes use AJAX outside the form)
    document.querySelectorAll('[name="add"], [data-add-to-cart], button[type="submit"][form*="product"]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        if (hasValidConsultation()) return;
        e.preventDefault();
        e.stopImmediatePropagation();
        const currentVariantId = getVariantIdFromForm(productForm);
        showGateModal({
          productTitle: productTitle,
          onProceed: () => {
            window.location.href = gateRedirectUrl(window.location.pathname, currentVariantId);
          }
        });
      }, true);
    });
  }

  /* ========== Cart page gating ========== */
  async function isCartGated() {
    try {
      const res = await fetch('/cart.js');
      const cart = await res.json();
      if (!cart.items || cart.items.length === 0) return false;

      for (const item of cart.items) {
        const handle = item.handle;
        if (!handle) continue;
        const cached = CACHE[handle];
        if (cached && (Date.now() - cached.fetchedAt < CACHE_TTL)) {
          if (cached.gated) return true;
          continue;
        }
        try {
          const pres = await fetch(`/products/${handle}.js`);
          const product = await pres.json();
          // Normalise tags to lower-case so tag checks are case-insensitive.
          const tags = (product.tags || []).map(function (t) { return String(t).toLowerCase(); });
          const isExempt = tags.indexOf('consultation-exempt') !== -1;
          // GATE RULE (rewritten 2026-08-24 — Dr Chris): a cart item is gated if,
          // and ONLY if, it carries the `requires-consultation` tag, which is
          // reserved for retinoid- or hydroquinone-containing products. The
          // previous product_type and vendor fallbacks gated the whole ZO / Obagi
          // range by brand and are removed. Do not reinstate them — tag instead.
          const isGated = !isExempt && tags.indexOf('requires-consultation') !== -1;
          CACHE[handle] = { gated: isGated, fetchedAt: Date.now() };
          if (isGated) return true;
        } catch (e) {}
      }
      return false;
    } catch (e) {
      return false;
    }
  }

  async function attachCartGate() {
    if (!/\/cart/.test(window.location.pathname)) return;
    if (hasValidConsultation()) return;

    const gated = await isCartGated();
    if (!gated) return;

    // Banner
    const cartContainer = document.querySelector('main') || document.body;
    injectBanner({ container: cartContainer, productTitle: '' });

    // Intercept checkout buttons
    document.querySelectorAll('[name="checkout"], [data-checkout], a[href*="/checkout"]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        if (hasValidConsultation()) return;
        e.preventDefault();
        e.stopImmediatePropagation();
        showGateModal({
          productTitle: '',
          onProceed: () => {
            window.location.href = gateRedirectUrl('/cart');
          }
        });
      }, true);
    });
  }

  /* ========== Inject minimal modal/banner CSS ========== */
  function injectStyles() {
    if (document.getElementById('regen-gate-styles')) return;
    const style = document.createElement('style');
    style.id = 'regen-gate-styles';
    style.textContent = `
      .regen-gate-banner {
        background: #fbf6ef;
        border: 1px solid #e6dccf;
        border-left: 3px solid #c4a876;
        padding: 1rem 1.25rem;
        margin: 0 0 1.5rem;
        font-family: 'Montserrat', system-ui, sans-serif;
        font-size: 0.92rem;
        color: #2d1f24;
        border-radius: 4px;
        line-height: 1.5;
      }
      .regen-gate-banner p { margin: 0; }
      .regen-gate-banner a { color: #2d1f24; text-decoration: underline; font-weight: 500; }

      .regen-gate-modal {
        position: fixed; inset: 0; z-index: 99999;
        display: flex; align-items: center; justify-content: center;
        font-family: 'Montserrat', system-ui, sans-serif;
        padding: 1rem;
      }
      .regen-gate-modal__backdrop {
        position: absolute; inset: 0;
        background: rgba(45, 31, 36, 0.55);
        backdrop-filter: blur(4px);
      }
      .regen-gate-modal__panel {
        position: relative;
        background: #fff;
        max-width: 460px; width: 100%;
        padding: 2.25rem 2rem;
        border-radius: 4px;
        box-shadow: 0 24px 60px rgba(45, 31, 36, 0.25);
        text-align: left;
      }
      .regen-gate-modal__kicker {
        font-size: 0.7rem;
        letter-spacing: 0.22em;
        text-transform: uppercase;
        color: #a88c5d;
        margin: 0 0 0.75rem;
        font-weight: 500;
      }
      .regen-gate-modal__panel h2 {
        font-family: 'Playfair Display', 'Times New Roman', serif;
        font-weight: 400;
        font-size: 1.65rem;
        color: #2d1f24;
        margin: 0 0 1rem;
        line-height: 1.2;
      }
      .regen-gate-modal__panel p {
        color: #6b5a5f;
        font-size: 0.95rem;
        line-height: 1.55;
        margin: 0 0 1rem;
      }
      .regen-gate-modal__time {
        font-size: 0.82rem;
        color: #a88c5d;
      }
      .regen-gate-modal__actions {
        display: flex; gap: 0.75rem;
        margin-top: 1.5rem;
        flex-wrap: wrap;
      }
      .regen-gate-modal__btn {
        flex: 1;
        min-width: 140px;
        padding: 0.85rem 1.25rem;
        font-family: inherit;
        font-size: 0.85rem;
        font-weight: 500;
        letter-spacing: 0.08em;
        text-transform: uppercase;
        cursor: pointer;
        border-radius: 4px;
        border: 1px solid #2d1f24;
        background: #fff;
        color: #2d1f24;
        transition: background 0.15s, color 0.15s;
      }
      .regen-gate-modal__btn--primary {
        background: #2d1f24;
        color: #fff;
      }
      .regen-gate-modal__btn--primary:hover { background: #4a3640; }
      .regen-gate-modal__btn--ghost:hover { background: #f0e6dc; }
    `;
    document.head.appendChild(style);
  }

  /* ========== Init ========== */
  function init() {
    injectStyles();
    attachProductGate();
    attachCartGate();
    syncCartAttribute();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

